import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion, onAnimationEnd, lockScroll, unlockScroll } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

const sheetQuery = window.matchMedia('(max-width: 1032px)');
const isSheetViewport = () => sheetQuery.matches;

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  #ensureDialogInDocument() {
    const el = document.getElementById('quick-add-dialog');
    if (el && !el.isConnected) document.body.appendChild(el);
    return el;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    sheetQuery.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    sheetQuery.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  #attachDialogToCard() {
    if (isSheetViewport()) return;

    const dialogComponent = document.getElementById('quick-add-dialog');
    const media = this.offsetParent instanceof HTMLElement ? this.offsetParent : this.closest('product-card');
    if (!dialogComponent || !media) return;

    if (getComputedStyle(media).position === 'static') {
      media.style.position = 'relative';
    }
    if (dialogComponent.parentElement !== media) {
      media.appendChild(dialogComponent);
    }
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('quick-add-dialog')) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    let quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    // A single-option card can resolve to an unavailable variant (e.g. re-selecting a
    // sold-out swatch). Keep "Choose" so shoppers reach the picker, not a dead-end disabled "Add".
    if (quickAddButton === 'add' && this.#isSelectedVariantUnavailable()) {
      quickAddButton = 'choose';
    }

    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Whether the card's currently selected swatch maps to an unavailable variant.
   * Reads `data-option-available` off the variant picker's selected option - the same
   * signal the product card uses. Only reports true on an explicit `false`, so an
   * unknown/absent signal leaves the caller's default ("add") untouched.
   * @returns {boolean}
   */
  #isSelectedVariantUnavailable() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === 'false';
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    const dialogComponent = this.#ensureDialogInDocument();
    if (!dialogComponent) {
      console.warn('[quick-add] #quick-add-dialog tidak ditemukan');
      return;
    }

    if (isSheetViewport()) {
      if (dialogComponent.parentElement !== document.body) {
        document.body.appendChild(dialogComponent);
      }
    } else {
      this.#attachDialogToCard();
    }

    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(/** @type {Element} */ (productGrid.cloneNode(true)));
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
    if (dialogComponent.refs?.dialog) dialogComponent.refs.dialog.scrollTop = 0;
  }


  /** @param {QuickAddDialog} dialogComponent */
  #activeDialogComponent = null;

  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);
    this.#activeDialogComponent = dialogComponent;

    const cleanup = () => {
      this.removeAttribute('stay-visible');
      dialogComponent.removeEventListener(DialogCloseEvent.eventName, cleanup);
      document.removeEventListener('pointerdown', handleOutsideClick);
    };

    const handleOutsideClick = (event) => {
      if (!this.closest('product-card')?.contains(event.target)) {
        cleanup();
      }
    };

    dialogComponent.addEventListener(DialogCloseEvent.eventName, cleanup, {once: true});
    document.addEventListener('pointerdown', handleOutsideClick);
  }

  #hostMedia = null;

  #positionModalOverCard(dialog) {
    const media = this.offsetParent instanceof HTMLElement ? this.offsetParent : this.closest('product-card');
    if (!media) return;

    const isMobile = isMobileBreakpoint();
    const isFullWidth = isMobile && !!this.closest(
      '.product-grid__item--full-width, product-card:has(~ .product-grid__item--full-width)'
    );

    const cardWidth = media.offsetWidth;
    dialog.dataset.layout = isFullWidth ? 'full' : cardWidth < 320 ? 'compact' : 'normal';
    
    const side = isMobile ? 12 : isFullWidth ? 30 : 30;
    const bottom = isMobile ? 12 : isFullWidth ? 38 : 25;
    // const wantedHeight = isFullWidth ? 285 : 286;
    // const height = Math.min(wantedHeight, media.offsetHeight - bottom - 8);
    const maxHeight = media.offsetHeight - bottom - 8;

    if (getComputedStyle(media).position === 'static') {
      media.style.position = 'relative';
    }

    const dialogComponent = dialog.closest('quick-add-dialog');
    if (dialogComponent && dialogComponent.parentElement !== media) {
      media.appendChild(dialogComponent);
    }
    this.#hostMedia = media;

    const set = (prop, value) => dialog.style.setProperty(prop, value, 'important');
    set('position', 'absolute');
    set('z-index', '4');
    set('margin', '0');
    set('right', 'auto');
    set('top', 'auto');
    set('left', `${side}px`);
    set('bottom', `${bottom}px`);
    set('width', `calc(100% - ${side * 2}px)`);
    set('max-width', 'none');
    set('height', 'auto');
    set('max-height', `${maxHeight}px`);
  }

  #closeWhenPointerLeaves(card, dialog, dialogComponent) {
    if (!card) return;

    const ac = new AbortController();
    const { signal } = ac;

    const close = () => {
      ac.abort();
      this.#closeQuickAddModal();
    };

    card.addEventListener('mouseleave', close, { signal });

    document.addEventListener(
      'pointerdown',
      (event) => {
        if (card.contains(event.target)) return;
        close();
      },
      { signal }
    );

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => ac.abort(), { once: true });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;

    dialog.removeAttribute('style');
    if (!dialog.closest('.atc-drawer__rec-grid')) {
      dialog.style.removeProperty('--qa-cols');
      dialog.style.removeProperty('--qa-box-gap');
      dialog.style.removeProperty('--qa-box-h');
    }

    const isSheet = isSheetViewport();
    dialog.dataset.mode = isSheet ? 'sheet' : 'popover';

    if (isSheet) {
      delete dialog.dataset.layout;
      if (dialogComponent.parentElement !== document.body) {
        document.body.appendChild(dialogComponent);
      }
    } else {
      this.#positionModalOverCard(dialog);
    }

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    if (isSheet) {
      this.#closeOnBackdropClick(dialog, dialogComponent);
    } else {
      this.#closeWhenPointerLeaves(this.closest('product-card'), dialog, dialogComponent);
    }

    const modalContent = document.getElementById('quick-add-modal-content');
    if (modalContent) this.#startSizeCollapse(modalContent);
    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.#sizeCollapseAC?.abort(), {
      once: true,
    });

    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeOnBackdropClick(dialog, dialogComponent) {
    const ac = new AbortController();

    dialog.addEventListener(
      'click',
      (event) => {
        if (event.target !== dialog) return; 
        const r = dialog.getBoundingClientRect();
        const inside =
          event.clientX >= r.left && event.clientX <= r.right && event.clientY >= r.top && event.clientY <= r.bottom;
        if (inside) return;
        ac.abort();
        this.#closeQuickAddModal();
      },
      { signal: ac.signal }
    );

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => ac.abort(), { once: true });
  }

  /* Kotak size */
  #sizesExpanded = false;
  #sizeCollapseAC = null;

  #applySizeCollapse(modalContent) {
    modalContent.querySelectorAll('fieldset').forEach((fieldset) => {
      const labels = [...fieldset.querySelectorAll('label')].filter(
        (l) => l.control instanceof HTMLInputElement && l.control.type === 'radio'
      );
      const next = fieldset.nextElementSibling;
      let btn = next?.classList.contains('qa-size-more') ? next : null;

      if (labels.length < 2) {
        btn?.remove();
        return;
      }

      let grid = labels[0].parentElement;
      while (grid && grid !== fieldset && !labels.every((l) => grid.contains(l))) {
        grid = grid.parentElement;
      }
      if (!grid) return;
      grid.classList.add('qa-size-grid');

      const items = [
        ...new Set(
          labels.map((l) => {
            let el = l;
            while (el.parentElement && el.parentElement !== grid) el = el.parentElement;
            return el;
          })
        ),
      ];

      const cols = Number(getComputedStyle(grid).getPropertyValue('--qa-cols')) || 4;
      const maxVisible = cols * 2;

      if (items.length <= maxVisible) {
      items.forEach((el) => el.classList.remove('qa-size-hidden'));
        btn?.remove();
        return;
      }

      items.forEach((el, i) =>
        el.classList.toggle('qa-size-hidden', !this.#sizesExpanded && i >= maxVisible)
      );

      if (!btn) {
        btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'quick-add-modal-size-guide-btn qa-size-more';
        btn.addEventListener('click', () => {
          this.#sizesExpanded = !this.#sizesExpanded;
          this.#applySizeCollapse(modalContent);
        });
        fieldset.after(btn);
      }
      btn.textContent = this.#sizesExpanded ? 'See less' : 'See more';
      btn.setAttribute('aria-expanded', String(this.#sizesExpanded));
    });
  }

  #startSizeCollapse(modalContent) {
    this.#sizeCollapseAC?.abort();
    const ac = new AbortController();
    this.#sizeCollapseAC = ac;
    this.#sizesExpanded = false;

    let raf = 0;
    const mo = new MutationObserver(() => schedule());
    const observe = () =>
      mo.observe(modalContent, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });

    const run = () => {
      raf = 0;
      mo.disconnect(); 
      this.#applySizeCollapse(modalContent);
      observe();
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(run);
    };

    observe();
    window.addEventListener('resize', schedule, { signal: ac.signal });
    ac.signal.addEventListener(
      'abort',
      () => {
        mo.disconnect();
        cancelAnimationFrame(raf);
      },
      { once: true }
    );

    run();
  }

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    document.querySelectorAll('quick-add-component[stay-visible]').forEach((el) => {
      el.removeAttribute('stay-visible');
    });

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!productGrid || !modalContent) return;

    const sizeGuideTitle =
      productGrid.querySelector('[id^="size-guide-title"]')?.textContent.trim() || 'Size guidance';
    const sizeGuideImages = [...productGrid.querySelectorAll('aside[id^="size-guide-panel"] img')].map((img) => ({
      src: img.getAttribute('src'),
      alt: img.getAttribute('alt') || sizeGuideTitle,
    }));

    [...productGrid.querySelectorAll('*')]
      .filter((el) => el.localName.startsWith('size-guide-drawer'))
      .forEach((el) => el.remove());

    const unwantedSelectors = [
      'media-gallery',
      '.product-information__media',
      '#product-description-anchor',
      '.product-description-wrapper',
      '.product-accordion-wrapper',
      'product-price',
      '.view-product-title',
      '.product-header',
      '.quantity-selector-wrapper',
      '#sticky-atc-bar',
      '[class*="breadcrumb"]',
      '.divider',
      '[id*="product_family"]',
    ];
    unwantedSelectors.forEach((selector) => {
      productGrid.querySelectorAll(selector).forEach((el) => el.remove());
    });

    productGrid.querySelectorAll('h1').forEach((h1) => (h1.closest('.text-block') || h1).remove());

    productGrid.querySelectorAll('a[href="#product-description-anchor"]').forEach((a) => {
      (a.parentElement || a).remove();
    });

    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    productGrid.querySelectorAll('.product-details').forEach((el) => {
      el.classList.remove('sticky-content--desktop');
    });

    morph(modalContent, productGrid);

    modalContent.querySelectorAll('.quick-add-modal-size-guide-btn').forEach((el) => el.remove());

    const buyButtonBlock = modalContent.querySelector('.buy-buttons-block, product-form-component');
    if (buyButtonBlock && sizeGuideImages.length) {
      const guideBtn = document.createElement('button');
      guideBtn.type = 'button';
      guideBtn.className = 'quick-add-modal-size-guide-btn';
      guideBtn.textContent = 'Size guide';
      guideBtn.addEventListener('click', () => this.#openSizeGuide(sizeGuideTitle, sizeGuideImages));
      buyButtonBlock.parentNode.insertBefore(guideBtn, buyButtonBlock);
    }

    this.#syncVariantSelection(modalContent);
  }

  #openSizeGuide(title, images) {
    let dlg = /** @type {HTMLDialogElement | null} */ (document.getElementById('quick-add-size-guide'));

    if (!dlg) {
      dlg = document.createElement('dialog');
      dlg.id = 'quick-add-size-guide';
      dlg.className = 'qa-size-guide';
      dlg.setAttribute('aria-labelledby', 'quick-add-size-guide-title');
      dlg.innerHTML = `
        <header class="qa-size-guide__header">
          <h2 class="qa-size-guide__title" id="quick-add-size-guide-title"></h2>
          <button type="button" class="qa-size-guide__close" aria-label="Close size guidance">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"></path></svg>
          </button>
        </header>
        <div class="qa-size-guide__content"></div>
      `;
      document.body.appendChild(dlg);

      const close = () => {
        if (!dlg.open || dlg.classList.contains('is-closing')) return;

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          dlg.close();
          return;
        }

        dlg.classList.add('is-closing');
        const finish = () => {
          dlg.classList.remove('is-closing');
          dlg.close();
        };
        dlg.addEventListener(
          'animationend',
          (e) => {
            if (e.target === dlg) finish();
          },
          { once: true }
        );
      };

      dlg.querySelector('.qa-size-guide__close')?.addEventListener('click', close);
      dlg.addEventListener('click', (e) => {
        if (e.target === dlg) close();
      });
      dlg.addEventListener('cancel', (e) => {
        e.preventDefault();
        close();
      });
      dlg.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') e.stopPropagation();
      });
    }

    dlg.querySelector('.qa-size-guide__title').textContent = title;
    const content = dlg.querySelector('.qa-size-guide__content');
    content.replaceChildren(
      ...images.map(({ src, alt }) => {
        const img = document.createElement('img');
        img.className = 'qa-size-guide__image';
        img.src = src;
        img.alt = alt;
        return img;
      })
    );

    dlg.showModal();
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  #previousScrollY = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.addEventListener('click', this.#handleCloseButtonClick);
    this.addEventListener('click', this.#blockEmptyClicks);
    this.addEventListener('submit', this.#blockWhileLoading, true);
    this.addEventListener('click', this.#blockWhileLoading, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.removeEventListener('click', this.#handleCloseButtonClick);
    this.removeEventListener('click', this.#blockEmptyClicks);
    this.removeEventListener('submit', this.#blockWhileLoading, true);
    this.removeEventListener('click', this.#blockWhileLoading, true);
  }

  showDialog() {
    const { dialog } = this.refs;
    if (dialog.open) return;
    if (dialog.dataset.mode === 'sheet') {
      this.#previousScrollY = getScrollTop();
      lockScroll(dialog);
      dialog.showModal();
      dialog.addEventListener('cancel', this.#handleCancel);
    } else {
      dialog.show(); 
    }

    this.dispatchEvent(new DialogOpenEvent());
    document.addEventListener('keydown', this.#handleEscape);
  }

  closeDialog = async () => {
    const { dialog } = this.refs;
    if (!dialog.open || dialog.classList.contains('dialog-closing')) return;

    document.removeEventListener('keydown', this.#handleEscape);

    dialog.style.animation = 'none';
    void dialog.offsetWidth;
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, { subtree: false });

    if (dialog.dataset.mode === 'sheet') {
      unlockScroll(dialog);
      scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    }

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  // Loading
  #pendingVariantLoads = 0;

  #startVariantLoading() {
    this.#pendingVariantLoads++;
    this.setAttribute('data-variant-loading', '');
  }

  #endVariantLoading() {
    this.#pendingVariantLoads = Math.max(0, this.#pendingVariantLoads - 1);
    if (this.#pendingVariantLoads === 0) this.removeAttribute('data-variant-loading');
  }

  #resetVariantLoading() {
    this.#pendingVariantLoads = 0;
    this.removeAttribute('data-variant-loading');
  }

  #blockWhileLoading = (event) => {
    if (!this.hasAttribute('data-variant-loading')) return;

    if (event.type === 'click') {
      const target = event.target;
      if (!(target instanceof Element) || !target.closest('.add-to-cart-button')) return;
    }

    event.preventDefault();
    event.stopPropagation();
  };

  #handleCancel = (event) => {
    event.preventDefault();
    this.closeDialog();
  };

  #handleCloseButtonClick = (event) => {
    if (event.target instanceof Element && event.target.closest('.quick-add-modal__close')) {
      this.closeDialog();
    }
  };

  #handleEscape = (event) => {
    if (event.key === 'Escape') this.closeDialog();
  };

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    event.stopPropagation();

    this.#startVariantLoading();
    // Wait for variant update data
    Promise.resolve(event.promise)
      .then(({ detail } = {}) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      })
      .finally(() => {
        requestAnimationFrame(() => this.#endVariantLoading());
      });
  };

  #handleDialogClose = () => {
    this.#resetVariantLoading();
    const modalContent = this.querySelector('#quick-add-modal-content');
    if (modalContent) {
      modalContent.innerHTML = '';
    }

    if (isSheetViewport() && this.parentElement !== document.body) {
      document.body.appendChild(this);
    }

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };

  #blockEmptyClicks = (event) => {
    const t = event.target;
    if (!(t instanceof Element)) return;
    if (t.closest('button, a, input, label, select, textarea, summary, [role="button"]')) return;
    event.stopPropagation();
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
